import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import React, { useMemo } from 'react'

import { CCEmailsSection } from 'components/common/CCEmailsSection'
import { Flow } from 'constants/misc'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUpdateCcEmails } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component User CC Emails settings displayed in Profile page
 * @example
 * <CCEmails />
 */
export const CCEmails: React.FC = () => {
  const { t } = useTranslation(['profile'])
  const { clientData } = useUserData()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const clientCcEmails = useMemo(() => clientData?.ccEmails ?? [], [clientData?.ccEmails])
  const updateClientCcEmails = useUpdateCcEmails()

  const mutateEmails = (emails: string[], analyticsEvent: AnalyticsEvent) => {
    logAnalyticsEvent(analyticsEvent, { Flow: Flow.PROFILE })
    updateClientCcEmails.mutate(
      { ccEmails: emails },
      {
        onSuccess: () => {
          spawnSuccessToast(t('forward_emails.update_success'))
        },
        onError: (error) => {
          logger.error('Failed to update profile CC emails', error, { context: { component: 'CCEmails' } })
          spawnErrorToast(t('forward_emails.update_error'))
        },
      }
    )
  }

  return (
    <CCEmailsSection
      title={t('forward_emails.title')}
      description={t('forward_emails.description')}
      inputLabel={t('forward_emails.input_label')}
      ccEmails={clientCcEmails}
      isPending={updateClientCcEmails.isPending}
      onAdd={(emails) => mutateEmails(emails, AnalyticsEvent.CC_EMAIL_ADDED)}
      onDelete={(email) => mutateEmails(clientCcEmails.filter((prevEmail) => prevEmail !== email), AnalyticsEvent.CC_EMAIL_REMOVED)}
    />
  )
}
